import { useQuery } from "@apollo/client";
import { useUserProfile } from "../hooks/useUserProfile";
import { useAccountContext } from "../context/AccountContext/useAccountContext";
import {
  GET_AVAILABLE_ACCOUNT_CONTEXTS,
  GetAvailableAccountContextsData,
} from "common/graphql/queries/GetAvailableAccountContexts";

/**
 * Returns `true` when {@link AccountContextBanner} would render. Both layouts
 * (for top-padding reservation) and the banner itself read this so visibility
 * and spacing reservation share a single source of truth — no risk of the
 * layout reserving 32px while the banner returns null, or vice versa.
 *
 * The predicate folds in the `activeDisplayName` lookup because the banner
 * can't render without one. That covers the brief window after an account
 * switch where a stored `contextId` doesn't yet match any entry in
 * `availableAccountContexts`; `AccountContextInitializer` clears the stale
 * ID on the next tick, but until it does, both layout and banner stay
 * collapsed in lockstep.
 *
 * Reads `AvailableAccountContexts` via Apollo's default `cache-first` policy.
 * `AccountContextInitializer` fires the same query upstream, so this hook
 * almost always serves from cache — but on a cold mount it will fire its
 * own network request (deduplicated with any concurrent in-flight query).
 */
export function useAccountContextBannerVisible(): boolean {
  const { userProfile, loading: profileLoading } = useUserProfile();
  const { contextId } = useAccountContext();
  const { data, loading: queryLoading } = useQuery<GetAvailableAccountContextsData>(GET_AVAILABLE_ACCOUNT_CONTEXTS);

  if (queryLoading || profileLoading || !userProfile || !data) {
    return false;
  }
  if (data.availableAccountContexts.length <= 1) {
    return false;
  }

  const activeId = contextId ?? userProfile.account.id;
  return data.availableAccountContexts.some((ctx) => ctx.id === activeId);
}

export default useAccountContextBannerVisible;
